import { FC, useState } from 'react';
import {
    Box,
    Button,
    Card,
    CardContent,
    CircularProgress,
    IconButton,
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableRow,
    TablePagination,
    TextField,
    Chip,
    Alert,
    Typography,
    FormControl,
    InputLabel,
    InputAdornment,
    Select,
    MenuItem,
    Checkbox,
    Menu,
    ListItemIcon,
    ListItemText,
} from '@mui/material';
import {
    Visibility as VisibilityIcon,
    VisibilityOff,
    Edit,
    Delete,
    Restore,
    DeleteForever,
    Add,
    Search as SearchIcon,
} from '@mui/icons-material';
import { Visibility } from '../../types/wordbookGroup';
import { useNavigate } from 'react-router-dom';
import {
    useWordbookGroupList,
    useDeleteWordbookGroup,
    usePermanentDeleteWordbookGroup,
    useRestoreWordbookGroup,
    useToggleWordbookGroupVisibility,
    useBulkDeleteWordbookGroups,
    useBulkUpdateGroupVisibility,
} from '../../hooks/useWordbookGroup';
import { formatKstDate } from '../../utils/dateTime';

export const WordbookGroupList: FC = () => {
    const navigate = useNavigate();
    const [page, setPage] = useState(0);
    const [size, setSize] = useState(25);
    const [searchInput, setSearchInput] = useState('');
    const [search, setSearch] = useState('');
    const [visibilityFilter, setVisibilityFilter] = useState<Visibility | ''>('');
    const [isLibraryFilter, setIsLibraryFilter] = useState<boolean | ''>('');
    const [includeDeleted, setIncludeDeleted] = useState(true);
    const [selectedIds, setSelectedIds] = useState<number[]>([]);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const triggerSearch = () => {
        setSearch(searchInput.trim());
        setPage(0);
    };

    // 데이터 조회
    const { data, isLoading, error } = useWordbookGroupList({
        page: page + 1,
        size,
        searchKeyword: search || undefined,
        visibility: visibilityFilter || undefined,
        isLibrary: isLibraryFilter === '' ? undefined : isLibraryFilter,
        includeDeleted,
        sortBy: 'createdAt',
        sortDirection: 'desc',
    });

    // Mutations
    const deleteGroup = useDeleteWordbookGroup();
    const permanentDelete = usePermanentDeleteWordbookGroup();
    const restoreGroup = useRestoreWordbookGroup();
    const toggleVisibility = useToggleWordbookGroupVisibility();
    const bulkDelete = useBulkDeleteWordbookGroups();
    const bulkUpdateVisibility = useBulkUpdateGroupVisibility();

    const handleToggleVisibility = (id: number) => {
        toggleVisibility.mutate(id);
    };

    const handleDelete = (id: number) => {
        if (confirm('그룹을 삭제하시겠습니까? 그룹에 속한 단어장들은 그룹에서 제외됩니다.')) {
            deleteGroup.mutate(id);
        }
    };

    const handlePermanentDelete = (id: number) => {
        if (confirm('⚠️ 영구 삭제하시겠습니까? 이 작업은 되돌릴 수 없습니다.')) {
            permanentDelete.mutate(id);
        }
    };

    const handleRestore = (id: number) => {
        restoreGroup.mutate(id);
    };

    const handleSelectAll = (checked: boolean) => {
        if (checked) {
            setSelectedIds(groups.map(g => g.id));
        } else {
            setSelectedIds([]);
        }
    };

    const handleSelectOne = (id: number, checked: boolean) => {
        if (checked) {
            setSelectedIds([...selectedIds, id]);
        } else {
            setSelectedIds(selectedIds.filter(selectedId => selectedId !== id));
        }
    };

    const handleBulkDelete = () => {
        if (selectedIds.length === 0) return;
        if (confirm(`선택한 ${selectedIds.length}개의 그룹을 삭제하시겠습니까?`)) {
            bulkDelete.mutate(selectedIds, {
                onSuccess: () => {
                    setSelectedIds([]);
                },
            });
        }
    };

    const handleBulkVisibility = (visibility: Visibility) => {
        if (selectedIds.length === 0) return;
        bulkUpdateVisibility.mutate(
            { groupIds: selectedIds, visibility },
            {
                onSuccess: () => {
                    setSelectedIds([]);
                    setAnchorEl(null);
                },
            }
        );
    };

    const groups = data?.content || [];
    const totalCount = data?.totalCount || 0;

    return (
        <Box p={3}>
            <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                <Typography variant="h4">
                    단어장 그룹 관리
                </Typography>
                <Button
                    variant="contained"
                    startIcon={<Add />}
                    onClick={() => navigate('/wordbook-group/create')}
                >
                    새 그룹 추가
                </Button>
            </Box>

            <Card>
                <CardContent>
                    {/* 필터 영역 */}
                    <Box display="flex" gap={2} mb={2} flexWrap="wrap">
                        <TextField
                            label="검색"
                            value={searchInput}
                            onChange={(e) => setSearchInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                    triggerSearch();
                                }
                            }}
                            size="small"
                            sx={{ width: 250 }}
                            placeholder="제목 검색 후 Enter"
                            slotProps={{
                                input: {
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton
                                                size="small"
                                                onClick={triggerSearch}
                                                edge="end"
                                            >
                                                <SearchIcon fontSize="small" />
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                },
                            }}
                        />
                        <FormControl size="small" sx={{ width: 150 }}>
                            <InputLabel>노출 상태</InputLabel>
                            <Select
                                value={visibilityFilter}
                                onChange={(e) => {
                                    setVisibilityFilter(e.target.value as Visibility | '');
                                    setPage(0);
                                }}
                                label="노출 상태"
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value={Visibility.VISIBLE}>노출</MenuItem>
                                <MenuItem value={Visibility.HIDDEN}>숨김</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ width: 150 }}>
                            <InputLabel>라이브러리</InputLabel>
                            <Select
                                value={isLibraryFilter}
                                onChange={(e) => {
                                    setIsLibraryFilter(e.target.value as boolean | '');
                                    setPage(0);
                                }}
                                label="라이브러리"
                            >
                                <MenuItem value="">전체</MenuItem>
                                <MenuItem value="true">라이브러리</MenuItem>
                                <MenuItem value="false">일반</MenuItem>
                            </Select>
                        </FormControl>
                        <FormControl size="small" sx={{ width: 150 }}>
                            <InputLabel>삭제 포함</InputLabel>
                            <Select
                                value={includeDeleted}
                                onChange={(e) => {
                                    setIncludeDeleted(e.target.value as boolean);
                                    setPage(0);
                                }}
                                label="삭제 포함"
                            >
                                <MenuItem value="true">포함</MenuItem>
                                <MenuItem value="false">제외</MenuItem>
                            </Select>
                        </FormControl>
                    </Box>

                    {/* 일괄 작업 버튼 */}
                    {selectedIds.length > 0 && (
                        <Box display="flex" gap={1} mb={2}>
                            <Chip
                                label={`${selectedIds.length}개 선택됨`}
                                onDelete={() => setSelectedIds([])}
                                color="primary"
                            />
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={handleBulkDelete}
                                color="error"
                            >
                                일괄 삭제
                            </Button>
                            <Button
                                size="small"
                                variant="outlined"
                                onClick={(e) => setAnchorEl(e.currentTarget)}
                            >
                                일괄 노출 설정
                            </Button>
                            <Menu
                                anchorEl={anchorEl}
                                open={Boolean(anchorEl)}
                                onClose={() => setAnchorEl(null)}
                            >
                                <MenuItem onClick={() => handleBulkVisibility(Visibility.VISIBLE)}>
                                    <ListItemIcon><VisibilityIcon fontSize="small" /></ListItemIcon>
                                    <ListItemText>노출</ListItemText>
                                </MenuItem>
                                <MenuItem onClick={() => handleBulkVisibility(Visibility.HIDDEN)}>
                                    <ListItemIcon><VisibilityOff fontSize="small" /></ListItemIcon>
                                    <ListItemText>숨김</ListItemText>
                                </MenuItem>
                            </Menu>
                        </Box>
                    )}

                    {error && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {error.message || '데이터를 불러오는데 실패했습니다.'}
                        </Alert>
                    )}

                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                    <Checkbox
                                        checked={selectedIds.length === groups.length && groups.length > 0}
                                        indeterminate={selectedIds.length > 0 && selectedIds.length < groups.length}
                                        onChange={(e) => handleSelectAll(e.target.checked)}
                                    />
                                </TableCell>
                                <TableCell>ID</TableCell>
                                <TableCell>제목</TableCell>
                                <TableCell>소유자</TableCell>
                                <TableCell>언어</TableCell>
                                <TableCell>단어장 수</TableCell>
                                <TableCell>총 단어 수</TableCell>
                                <TableCell>상태</TableCell>
                                <TableCell>노출</TableCell>
                                <TableCell>라이브러리</TableCell>
                                <TableCell>생성일</TableCell>
                                <TableCell>액션</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {isLoading ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center">
                                        <Box display="flex" flexDirection="column" alignItems="center" py={4} gap={1}>
                                            <CircularProgress size={28} />
                                            <Typography variant="body2" color="text.secondary">
                                                그룹 목록 로딩 중...
                                            </Typography>
                                        </Box>
                                    </TableCell>
                                </TableRow>
                            ) : groups.length === 0 ? (
                                <TableRow>
                                    <TableCell colSpan={12} align="center">
                                        <Typography color="text.secondary" py={4}>
                                            등록된 그룹이 없습니다.
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            ) : (
                                groups.map((group) => (
                                    <TableRow
                                        key={group.id}
                                        hover
                                        sx={{
                                            cursor: 'pointer',
                                            opacity: group.deletedAt ? 0.5 : 1,
                                            backgroundColor: group.deletedAt ? '#ffebee' : 'inherit',
                                        }}
                                        onClick={() => navigate(`/wordbook-group/${group.id}`)}
                                    >
                                        <TableCell padding="checkbox" onClick={(e) => e.stopPropagation()}>
                                            <Checkbox
                                                checked={selectedIds.includes(group.id)}
                                                onChange={(e) => handleSelectOne(group.id, e.target.checked)}
                                            />
                                        </TableCell>
                                        <TableCell>{group.id}</TableCell>
                                        <TableCell>
                                            <Typography variant="body2" fontWeight={500}>
                                                {group.title}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {group.ownerName || '-'}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2">
                                                {group.headLang} → {group.meaningLang}
                                            </Typography>
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={group.totalWordbooks.toLocaleString()}
                                                size="small"
                                                color={group.totalWordbooks > 0 ? 'primary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            <Chip
                                                label={group.totalWords.toLocaleString()}
                                                size="small"
                                                color={group.totalWords > 0 ? 'secondary' : 'default'}
                                            />
                                        </TableCell>
                                        <TableCell>
                                            {group.deletedAt ? (
                                                <Chip label="삭제됨" size="small" color="error" />
                                            ) : (
                                                <Chip label="활성" size="small" color="success" />
                                            )}
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            {!group.deletedAt && (
                                                <IconButton
                                                    size="small"
                                                    onClick={() => handleToggleVisibility(group.id)}
                                                >
                                                    {group.visibility === Visibility.VISIBLE ? (
                                                        <VisibilityIcon color="primary" />
                                                    ) : (
                                                        <VisibilityOff />
                                                    )}
                                                </IconButton>
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            {group.libraryId != null && (
                                                <Chip label="라이브러리" size="small" color="secondary" />
                                            )}
                                        </TableCell>
                                        <TableCell>
                                            <Typography variant="body2" color="text.secondary">
                                                {formatKstDate(group.createdAt)}
                                            </Typography>
                                        </TableCell>
                                        <TableCell onClick={(e) => e.stopPropagation()}>
                                            <Box display="flex" gap={0.5}>
                                                {group.deletedAt ? (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleRestore(group.id)}
                                                            title="복원"
                                                            color="success"
                                                        >
                                                            <Restore />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handlePermanentDelete(group.id)}
                                                            title="영구 삭제"
                                                            color="error"
                                                        >
                                                            <DeleteForever />
                                                        </IconButton>
                                                    </>
                                                ) : (
                                                    <>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => navigate(`/wordbook-group/${group.id}/edit`)}
                                                            title="수정"
                                                        >
                                                            <Edit />
                                                        </IconButton>
                                                        <IconButton
                                                            size="small"
                                                            onClick={() => handleDelete(group.id)}
                                                            title="삭제"
                                                            color="error"
                                                        >
                                                            <Delete />
                                                        </IconButton>
                                                    </>
                                                )}
                                            </Box>
                                        </TableCell>
                                    </TableRow>
                                ))
                            )}
                        </TableBody>
                    </Table>

                    <TablePagination
                        component="div"
                        count={totalCount}
                        page={page}
                        onPageChange={(_, newPage) => setPage(newPage)}
                        rowsPerPage={size}
                        onRowsPerPageChange={(e) => {
                            setSize(parseInt(e.target.value, 10));
                            setPage(0);
                        }}
                        rowsPerPageOptions={[10, 25, 50, 100]}
                        labelRowsPerPage="페이지당 행 수:"
                        labelDisplayedRows={({ from, to, count }) =>
                            `${from}-${to} / 전체 ${count !== -1 ? count : `${to} 이상`}`
                        }
                    />
                </CardContent>
            </Card>
        </Box>
    );
};
